import { type Table } from "@tanstack/react-table";
import { Bug } from "lucide-react";
import { useState } from "react";

type StateKey =
  | "columnFilters"
  | "globalFilter"
  | "sorting"
  | "pagination"
  | "columnVisibility"
  | "columnOrder"
  | "rowSelection"
  | "expanded"
  | "grouping"
  | "columnPinning"
  | "columnSizing"
  | "columnSizingInfo"
  | "rowPinning";

const stateOptions: readonly StateKey[] = [
  "columnFilters",
  "globalFilter",
  "sorting",
  "pagination",
  "columnVisibility",
  "columnOrder",
  "rowSelection",
  "expanded",
  "grouping",
  "columnPinning",
  "columnSizing",
  "columnSizingInfo",
  "rowPinning"
];

type ModelKey = keyof Table<any>;

const modelOptions: readonly ModelKey[] = [
  "getRowModel",
  "getGroupedRowModel",
  "getPaginationRowModel",
  "getCoreRowModel",
  "getFilteredRowModel",
  "getFilteredSelectedRowModel",
  "getSortedRowModel",
  "getPreExpandedRowModel",
  "getGroupedSelectedRowModel",
  "getPrePaginationRowModel",
  "getPreSelectedRowModel",
  "getPreSortedRowModel",
  "getSelectedRowModel",
  "getExpandedRowModel",
  "getIsAllColumnsVisible",
  "getIsSomeColumnsPinned",
  "getIsSomeColumnsVisible",
  "getHeaderGroups",
  "getFlatHeaders",
  "getLeafHeaders",
  "getLeftFlatHeaders",
  "getCenterLeafHeaders",
  "getRightFlatHeaders",
  "getAllColumns",
  "getAllFlatColumns",
  "getAllLeafColumns",
  "getLeftLeafColumns",
  "getCenterLeafColumns",
  "getRightLeafColumns",
  "getVisibleFlatColumns",
  "getVisibleLeafColumns",
  "getLeftVisibleLeafColumns",
  "getRightVisibleLeafColumns",
  "getCenterVisibleLeafColumns"
];

const DataTableDevTool = ({ table }: { table: Table<any> }) => {
  const [open, setOpen] = useState(false);
  const [visibleStates, setVisibleStates] = useState<StateKey[]>([]);
  const [visibleModels, setVisibleModels] = useState<ModelKey[]>([]);
  const tableState = table.getState();

  const toggleValue = <U extends string>(arr: U[], value: U): U[] =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const getCircularReplacer = () => {
    const seen = new WeakSet();
    return (_key: string, value: any) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) return "[Circular]";
        seen.add(value);
      }
      return value;
    };
  };

  const renderStateValue = (key: StateKey) => {
    const value = tableState[key];
    if (value == null) return null;
    return (
      <div key={key} className="mt-4">
        <div className="font-bold capitalize">{key}</div>
        <pre className="bg-gray-100 text-xs p-2 rounded overflow-x-auto whitespace-pre-wrap">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  };

  const renderModelValue = (key: ModelKey) => {
    let value: unknown;
    try {
      value = (table as any)[key]();
    } catch {
      value = undefined;
    }
    if (value == null) return null;
    let jsonString = "";
    try {
      jsonString = JSON.stringify(value, getCircularReplacer(), 2);
    } catch {
      jsonString = "[Cannot stringify]";
    }

    return (
      <div key={key} className="mt-4">
        <div className="font-bold capitalize">{key}</div>
        <pre className="bg-gray-100 text-xs p-2 rounded overflow-x-auto whitespace-pre-wrap">
          {jsonString}
        </pre>
      </div>
    );
  };

  if (process.env.NODE_ENV === "production") return null;

  return (
    <div>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 rounded-full bg-white border shadow p-3"
        aria-label="Open debug panel"
      >
        <Bug className="w-5 h-5" />
      </button>

      {/* Sidebar Debug Panel */}
      {open && (
        <aside className="fixed top-0 right-0 h-full w-[420px] bg-white shadow-lg border-l border-gray-200 z-50 flex flex-col">
          {/* Header */}
          <header className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Debug Table State &amp; Models</h2>
            <button onClick={() => setOpen(false)} className="text-xl font-bold" aria-label="Close">
              ×
            </button>
          </header>

          {/* Main Content */}
          <main className="overflow-auto p-4 flex-grow space-y-4">
            <section className="max-h-80 overflow-y-auto overflow-x-hidden">
              {/* Select Table States */}
              <fieldset>
                <legend className="block text-sm font-medium mb-1">Show Table State</legend>
                <div className="space-y-1">
                  {stateOptions.map((stateKey) => (
                    <label
                      key={stateKey}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        className="accent-blue-500"
                        checked={visibleStates.includes(stateKey)}
                        onChange={() => setVisibleStates(toggleValue(visibleStates, stateKey))}
                      />
                      <span className="truncate">{stateKey}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Select Table Models */}
              <fieldset>
                <legend className="block text-sm font-medium mb-1">Show Table Models</legend>
                <div className="space-y-1">
                  {modelOptions.map((modelKey) => (
                    <label
                      key={modelKey}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        className="accent-green-500"
                        checked={visibleModels.includes(modelKey)}
                        onChange={() => setVisibleModels(toggleValue(visibleModels, modelKey))}
                      />
                      <span className="truncate">{modelKey}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </section>

            {/* JSON Outputs */}
            <hr className="my-2 border-gray-300" />
            {visibleStates.map(renderStateValue)}
            {visibleModels.map(renderModelValue)}
          </main>
        </aside>
      )}
    </div>
  );
};

export default DataTableDevTool;
